import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { calendarGrid, shiftDay } from './reservationList';
import { useWaiterReservationCountsQuery } from './waiterApi';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  /** Le jour affiché par la liste, `YYYY-MM-DD`. */
  day: string;
  today: string;
  onPick: (day: string) => void;
}

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

/**
 * SC-1612 — le calendrier du carnet : sous chaque jour, combien de
 * réservations et de personnes l'attendent (confirmées ou arrivées). Remplace
 * le sélecteur natif du téléphone, qui ne sait rien afficher dans ses cases.
 * Semaine commençant le lundi, six rangées fixes : la feuille ne change pas de
 * hauteur d'un mois à l'autre.
 */
export default function ReservationCalendar({ open, onClose, token, day, today, onPick }: Props) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(day.slice(0, 7));
  // Réouverte sur le jour affiché, pas sur le dernier mois feuilleté.
  const [openedFor, setOpenedFor] = useState(day);
  if (open && openedFor !== day) {
    setOpenedFor(day);
    setMonth(day.slice(0, 7));
  }

  const cells = useMemo(() => calendarGrid(month), [month]);
  const { data: counts, isError } = useWaiterReservationCountsQuery(
    { token, from: cells[0], to: cells[cells.length - 1] },
    { skip: !open, refetchOnMountOrArgChange: 30 },
  );

  const monthLabel = new Date(`${month}-15T12:00:00Z`).toLocaleDateString(i18n.language, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  // 2024-01-01 est un lundi : sept jours suivants = libellés lun.→dim.
  const weekdayLabel = (i: number) =>
    new Date(Date.UTC(2024, 0, 1 + i, 12)).toLocaleDateString(i18n.language, { weekday: 'narrow', timeZone: 'UTC' });
  const goMonth = (delta: number) => setMonth(shiftDay(`${month}-15`, delta * 30).slice(0, 7));

  const ARROW =
    'shrink-0 w-11 h-11 flex items-center justify-center rounded-full border-2 border-ink bg-paper font-sans text-xl font-bold text-ink';

  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('waiter.notes.reservations.dayPick')} instant>
      <div data-testid="reservations-calendar" className="p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="reservations-calendar-prev"
            aria-label={t('waiter.notes.reservations.monthPrev')}
            onClick={() => goMonth(-1)}
            className={ARROW}
          >
            <span aria-hidden="true" className="rtl:-scale-x-100">‹</span>
          </button>
          <h2
            data-testid="reservations-calendar-month"
            className="min-w-0 flex-1 text-center font-sans text-[18px] font-bold capitalize truncate"
          >
            {monthLabel}
          </h2>
          <button
            type="button"
            data-testid="reservations-calendar-next"
            aria-label={t('waiter.notes.reservations.monthNext')}
            onClick={() => goMonth(1)}
            className={ARROW}
          >
            <span aria-hidden="true" className="rtl:-scale-x-100">›</span>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center">
          {WEEKDAYS.map((i) => (
            <span key={i} className="font-sans text-xs font-semibold text-ink-mute uppercase">
              {weekdayLabel(i)}
            </span>
          ))}
          {cells.map((cell) => {
            const inMonth = cell.startsWith(month);
            const isPicked = cell === day;
            const isToday = cell === today;
            const c = counts?.[cell];
            return (
              <button
                key={cell}
                type="button"
                data-testid={`reservations-calendar-day-${cell}`}
                aria-pressed={isPicked}
                aria-label={
                  c
                    ? `${cell} — ${t('waiter.notes.reservations.calendarDay', { n: c.count, covers: c.covers })}`
                    : cell
                }
                onClick={() => {
                  onPick(cell);
                  onClose();
                }}
                className={`h-14 min-w-0 rounded-md flex flex-col items-center justify-start pt-1 border-2 ${
                  isPicked ? 'bg-ink text-paper border-ink' : isToday ? 'border-ink bg-paper' : 'border-transparent bg-paper'
                } ${inMonth || isPicked ? '' : 'opacity-40'} ${cell < today && !isPicked ? 'text-ink-mute' : ''}`}
              >
                <span className="font-sans text-base font-bold tabular-nums leading-none">{Number(cell.slice(8))}</span>
                {/* Deux lignes : à 360 px une case fait ~40 px, « 3 · 11 p. »
                    n'y tient pas sur une. */}
                {c && (
                  <span
                    data-testid="reservations-calendar-count"
                    className="flex flex-col items-center font-sans text-[11px] leading-tight tabular-nums"
                  >
                    <span className="font-bold">{c.count}</span>
                    <span className={isPicked ? '' : 'text-ink-mute'}>
                      {t('waiter.notes.reservations.calendarCovers', { n: c.covers })}
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="font-sans text-sm text-ink-mute">
          {isError ? t('waiter.notes.reservations.calendarError') : t('waiter.notes.reservations.calendarLegend')}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="reservations-calendar-today"
            onClick={() => {
              onPick(today);
              onClose();
            }}
            className="flex-1 h-12 rounded-btn border-2 border-ink bg-paper font-sans text-base font-bold text-ink"
          >
            {t('waiter.notes.reservations.today')}
          </button>
          <button
            type="button"
            data-testid="reservations-calendar-close"
            onClick={onClose}
            className="flex-1 h-12 rounded-btn bg-ink text-paper font-sans text-base font-bold"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
